import { ReactNode } from 'react';

import Formatted from '@flipster/react/design-utils/formatted';
import IdentificationIcon from '@flipster/react/icon/generated/IdentificationIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

interface SingleMissionCardProps {
  showExpiredTime: boolean;
  children: ReactNode;
}

export const SingleMissionCard = ({ showExpiredTime, children }: SingleMissionCardProps) => {
  return (
    <div
      className={css({
        borderTopRadius: showExpiredTime ? undefined : 8,
        borderBottomRadius: 8,
        py: 16,
        background: 'background.default.level-1',
        display: { tablet: 'flex' },
        alignItems: { tablet: 'center' },
        justifyContent: { tablet: 'center' },
        gap: { tablet: 20 },
        mb: { tablet: 16 },
        px: { base: 12, tablet: 32 },
      })}
    >
      {children}
    </div>
  );
};

interface SingleMissionRewardProps {
  rewardAmount: string;
  rewardCurrency: string | null;
  isInProgress: boolean;
  coinSrc?: string;
  tooltipSlot?: ReactNode;
}

export const SingleMissionReward = ({
  rewardAmount,
  rewardCurrency,
  isInProgress,
  coinSrc,
  tooltipSlot,
}: SingleMissionRewardProps) => {
  const { isMobile } = useBreakpoint();

  const amountCss: Styles = {
    textStyle: 'Title/L/Monospace',
    color: isInProgress ? 'content.accent.promotion' : 'content.default.level-1',
  };
  const currencyCss = css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', mt: 4 });

  if (isMobile) {
    return (
      <div className={hstack({ gap: 4, alignItems: 'start', mb: 16 })}>
        <div className={css({ flex: 1 })}>
          <Formatted.Number value={rewardAmount} precision={0} toFixed css={amountCss} />
          <div className={currencyCss}>{rewardCurrency}</div>
        </div>
        {tooltipSlot}
      </div>
    );
  }

  return (
    <div className={vstack({ gap: 2, alignItems: 'center', minW: 152, pr: 32 })}>
      {coinSrc && <img src={coinSrc} alt='mission icon' width={42} height={42} />}
      <Formatted.Number value={rewardAmount} precision={0} toFixed css={amountCss} />
      <div className={currencyCss}>{rewardCurrency}</div>
    </div>
  );
};

interface SingleMissionTitleContentProps {
  isExpired: boolean;
  progressType: string;
  isInProgress: boolean;
  missionTitle: string | undefined;
}

export const SingleMissionTitleContent = ({
  isExpired,
  progressType,
  isInProgress,
  missionTitle,
}: SingleMissionTitleContentProps) => (
  <>
    {!isExpired && progressType === 'KYCVerification' && (
      <IdentificationIcon
        size={20}
        color={isInProgress ? token('colors.content.default.level-5') : token('colors.content.default.level-1')}
        className={css({ display: { base: 'block', tablet: 'none' } })}
      />
    )}
    <div
      className={css({
        textStyle: 'Text/L/Strong',
        color: isInProgress ? 'content.default.level-5' : 'content.default.level-1',
      })}
    >
      {missionTitle}
    </div>
  </>
);
